/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { Serializable } from './serializable';
import { Observable } from 'rxjs';
import { EventEmitter } from '@angular/core';
import { DbFactory } from './factory/dbFactory';

export class DatabaseHelper {
  public db: IDBDatabase;
  public storeName: string;
  constructor(db: IDBDatabase, storeName: string) {
    this.db = db;
    this.storeName = storeName;
  }

  /**
   * Store array of serializable on the db
   * @param objectStore name of the store
   * @param data to store
   * @param onError : * break = Stop the storage if an error is occurred. Continue = continue the storage even if an error is occurred
   *
   */
  public store(
    objectStore: string,
    data: Serializable[],
    onError: 'break' | 'continue' = 'break'
  ): Observable<boolean> {
    const tx = this.db.transaction(objectStore, 'readwrite');
    const store = tx.objectStore(objectStore);
    const obs = new EventEmitter<boolean>(true);
    tx.oncomplete = () => {
      obs.next(true);
      obs.complete();
    };
    for (const d of data) {
      const request = store.put(d.serialize());
      request.onsuccess = (ev: any) => {
        d.setId(ev.srcElement.result);
      };
      request.onerror = (err: any) => {
        if (onError === 'continue') {
          err.preventDefault();
        } else {
          console.error(err);
        }
      };
    }
    return obs.asObservable();
  }

  /**
   * Remove the row identified by key (primary key)
   * @param objectStore : name of the store
   * @param key : primary key of the record
   */
  public remove(objectStore: string, key: any): Observable<boolean> {
    const obs = new EventEmitter<boolean>();
    const tx = this.db.transaction(objectStore, 'readwrite');
    const store = tx.objectStore(objectStore);
    store.delete(key);
    tx.oncomplete = () => {
      obs.next(true);
      obs.complete();
    };
    return obs.asObservable();
  }

  /**
   * Remove all rows respected index condition
   * @param objectStore : name of the store
   * @param indexName: string
   * @param key condition for index selection
   */
  public removeByIndex(
    objectStore: string,
    indexName: string,
    key: IDBKeyRange
  ): Observable<boolean> {
    const obs = new EventEmitter<boolean>();
    const tx = this.db.transaction(objectStore, 'readwrite');
    const store = tx.objectStore(objectStore);
    const index = store.index(indexName);
    index.openCursor(key).onsuccess = (event: any) => {
      const cursor = event.target.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      }
    };
    tx.oncomplete = () => {
      obs.next(true);
      obs.complete();
    };
    return obs.asObservable();
  }

  /**
   * Get an unique element by id (primary key)
   * @param factory to build element
   * @param id primary key of the record
   */
  public getById<T>(factory: DbFactory<T>, id: any): Observable<T> {
    const tx = this.db.transaction(factory.getStorageName(), 'readonly');
    const store = tx.objectStore(factory.getStorageName());
    const obs = new EventEmitter<T>(true);
    store.get(id).onsuccess = (event: any) => {
      if (event.srcElement.result) {
        obs.emit(factory.convertSerial(event.srcElement.result));
      } else {
        obs.emit(null);
      }
      obs.complete();
    };
    return obs;
  }

  /**
   * Get a serie of element by index defined on the store
   * @param factory to build element
   * @param indexName name of the index defined on the store
   * @param value to fill index condition
   */
  public getByIndex<T>(
    factory: DbFactory<T>,
    indexName: string,
    value: any | any[]
  ): Observable<T[]> {
    const tx = this.db.transaction(factory.getStorageName(), 'readonly');
    const store = tx.objectStore(factory.getStorageName());
    const obs = new EventEmitter<T[]>(true);
    const index = store.index(indexName);
    index.getAll(value).onsuccess = (event: any) => {
      const array = [];
      if (event.srcElement.result) {
        for (const o of event.srcElement.result) {
          array.push(factory.convertSerial(o));
        }
      }
      obs.emit(array);
      obs.complete();
    };
    return obs;
  }

  /**
   * Return all element stored on the store
   * @param factory to build elements
   */
  public getAll<T>(factory: DbFactory<T>): Observable<T[]> {
    const tx = this.db.transaction(factory.getStorageName(), 'readonly');
    const store = tx.objectStore(factory.getStorageName());
    const obs = new EventEmitter<any>(true);
    store.getAll().onsuccess = (event: any) => {
      const array: T[] = [];
      for (const o of event.srcElement.result) {
        array.push(factory.convertSerial(o));
      }
      obs.emit(array);
      obs.complete();
    };
    return obs;
  }

  /**
   * Remove all element on a store
   * @param storageName store to clear
   */
  public clear(storageName: string) {
    const tx = this.db.transaction(storageName, 'readwrite');
    const store = tx.objectStore(storageName);
    store.clear();
  }
}
